/**
 * Enhanced Consultation Service for Production Premium SaaS
 * Comprehensive API service for consultation management
 */
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const CONSULTATION_API = `${API_BASE_URL}/api/v1/consultations`;

class EnhancedConsultationService {
  /**
   * Get authentication headers
   */
  getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Get multipart headers for file uploads
   */
  getMultipartHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    };
  }

  // ==================== CONSULTATION SESSIONS ====================

  /**
   * Get all consultations with filters
   */
  async getConsultations(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${CONSULTATION_API}/sessions/?${params}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consultations:', error);
      throw error;
    }
  }

  /**
   * Get consultation by ID
   */
  async getConsultation(consultationId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/sessions/${consultationId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consultation:', error);
      throw error;
    }
  }

  /**
   * Create new consultation
   */
  async createConsultation(consultationData) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/sessions/`, consultationData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating consultation:', error);
      throw error;
    }
  }

  /**
   * Update consultation
   */
  async updateConsultation(consultationId, consultationData) {
    try {
      const response = await axios.patch(`${CONSULTATION_API}/sessions/${consultationId}/`, consultationData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating consultation:', error);
      throw error;
    }
  }

  /**
   * Delete consultation
   */
  async deleteConsultation(consultationId) {
    try {
      const response = await axios.delete(`${CONSULTATION_API}/sessions/${consultationId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting consultation:', error);
      throw error;
    }
  }

  /**
   * Start consultation
   */
  async startConsultation(consultationId) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/sessions/${consultationId}/start/`, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error starting consultation:', error);
      throw error;
    }
  }

  /**
   * Complete consultation
   */
  async completeConsultation(consultationId, completionData = {}) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/sessions/${consultationId}/complete/`, completionData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error completing consultation:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION TYPES ====================

  /**
   * Get all consultation types
   */
  async getConsultationTypes() {
    try {
      const response = await axios.get(`${CONSULTATION_API}/types/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consultation types:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION TEMPLATES ====================

  /**
   * Get all consultation templates
   */
  async getTemplates(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${CONSULTATION_API}/templates/?${params}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  /**
   * Get template by ID
   */
  async getTemplate(templateId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/templates/${templateId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template:', error);
      throw error;
    }
  }

  /**
   * Create new template
   */
  async createTemplate(templateData) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/templates/`, templateData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  /**
   * Update template
   */
  async updateTemplate(templateId, templateData) {
    try {
      const response = await axios.patch(`${CONSULTATION_API}/templates/${templateId}/`, templateData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  /**
   * Delete template
   */
  async deleteTemplate(templateId) {
    try {
      const response = await axios.delete(`${CONSULTATION_API}/templates/${templateId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  /**
   * Use template
   */
  async useTemplate(templateId) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/templates/${templateId}/use/`, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error using template:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION DOCUMENTS ====================

  /**
   * Get consultation documents
   */
  async getDocuments(consultationId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/documents/?consultation=${consultationId}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  /**
   * Add document to consultation
   */
  async addDocument(consultationId, documentData) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/sessions/${consultationId}/add-document/`, documentData, {
        headers: this.getMultipartHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error adding document:', error);
      throw error;
    }
  }

  /**
   * Get document by ID
   */
  async getDocument(documentId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/documents/${documentId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  }

  /**
   * Update document
   */
  async updateDocument(documentId, documentData) {
    try {
      const response = await axios.patch(`${CONSULTATION_API}/documents/${documentId}/`, documentData, {
        headers: this.getMultipartHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  /**
   * Delete document
   */
  async deleteDocument(documentId) {
    try {
      const response = await axios.delete(`${CONSULTATION_API}/documents/${documentId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  /**
   * Verify document
   */
  async verifyDocument(documentId) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/documents/${documentId}/verify/`, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying document:', error);
      throw error;
    }
  }

  /**
   * Download document
   */
  async downloadDocument(documentId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/documents/${documentId}/download/`, {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION REMINDERS ====================

  /**
   * Get consultation reminders
   */
  async getReminders(consultationId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/reminders/?consultation=${consultationId}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching reminders:', error);
      throw error;
    }
  }

  /**
   * Schedule reminder
   */
  async scheduleReminder(consultationId, reminderData) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/sessions/${consultationId}/schedule-reminder/`, reminderData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error scheduling reminder:', error);
      throw error;
    }
  }

  /**
   * Get reminder by ID
   */
  async getReminder(reminderId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/reminders/${reminderId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching reminder:', error);
      throw error;
    }
  }

  /**
   * Update reminder
   */
  async updateReminder(reminderId, reminderData) {
    try {
      const response = await axios.patch(`${CONSULTATION_API}/reminders/${reminderId}/`, reminderData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating reminder:', error);
      throw error;
    }
  }

  /**
   * Delete reminder
   */
  async deleteReminder(reminderId) {
    try {
      const response = await axios.delete(`${CONSULTATION_API}/reminders/${reminderId}/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting reminder:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION ANALYTICS ====================

  /**
   * Get consultation analytics
   */
  async getAnalytics(consultationId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/sessions/${consultationId}/analytics/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }

  /**
   * Update consultation analytics
   */
  async updateAnalytics(consultationId, analyticsData) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/sessions/${consultationId}/update-analytics/`, analyticsData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating analytics:', error);
      throw error;
    }
  }

  /**
   * Get consultation dashboard data
   */
  async getDashboard() {
    try {
      const response = await axios.get(`${CONSULTATION_API}/dashboard/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      throw error;
    }
  }

  /**
   * Get analytics summary
   */
  async getAnalyticsSummary() {
    try {
      const response = await axios.get(`${CONSULTATION_API}/analytics-summary/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION WORKFLOW ====================

  /**
   * Get consultation workflow steps
   */
  async getWorkflowSteps(consultationId) {
    try {
      const response = await axios.get(`${CONSULTATION_API}/sessions/${consultationId}/workflow/`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow steps:', error);
      throw error;
    }
  }

  /**
   * Update workflow step
   */
  async updateWorkflowStep(consultationId, stepData) {
    try {
      const response = await axios.post(`${CONSULTATION_API}/sessions/${consultationId}/workflow/`, stepData, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating workflow step:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION SEARCH & FILTERS ====================

  /**
   * Search consultations
   */
  async searchConsultations(query, filters = {}) {
    try {
      const params = new URLSearchParams({
        search: query,
        ...filters
      });
      const response = await axios.get(`${CONSULTATION_API}/sessions/?${params}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error searching consultations:', error);
      throw error;
    }
  }

  /**
   * Get consultations by date range
   */
  async getConsultationsByDateRange(startDate, endDate, filters = {}) {
    try {
      const params = new URLSearchParams({
        scheduled_start__gte: startDate,
        scheduled_start__lte: endDate,
        ...filters
      });
      const response = await axios.get(`${CONSULTATION_API}/sessions/?${params}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consultations by date range:', error);
      throw error;
    }
  }

  /**
   * Get consultations by status
   */
  async getConsultationsByStatus(status, filters = {}) {
    try {
      const params = new URLSearchParams({
        status: status,
        ...filters
      });
      const response = await axios.get(`${CONSULTATION_API}/sessions/?${params}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consultations by status:', error);
      throw error;
    }
  }

  /**
   * Get consultations by priority
   */
  async getConsultationsByPriority(priority, filters = {}) {
    try {
      const params = new URLSearchParams({
        priority: priority,
        ...filters
      });
      const response = await axios.get(`${CONSULTATION_API}/sessions/?${params}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consultations by priority:', error);
      throw error;
    }
  }

  // ==================== CONSULTATION EXPORTS ====================

  /**
   * Export consultations to CSV
   */
  async exportConsultationsToCSV(filters = {}) {
    try {
      const params = new URLSearchParams({
        format: 'csv',
        ...filters
      });
      const response = await axios.get(`${CONSULTATION_API}/sessions/export/?${params}`, {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting consultations:', error);
      throw error;
    }
  }

  /**
   * Export consultation report
   */
  async exportConsultationReport(consultationId, format = 'pdf') {
    try {
      const response = await axios.get(`${CONSULTATION_API}/sessions/${consultationId}/export/?format=${format}`, {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting consultation report:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const consultationService = new EnhancedConsultationService();
export { consultationService };
export default consultationService;


















